import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyState } from '../components/common/EmptyState';
import { StageStepper } from '../components/lifecycle/StageStepper';
import { GatePanel } from '../components/lifecycle/GatePanel';
import { AssessmentPanel } from '../components/lifecycle/panels/AssessmentPanel';
import { BidPanel } from '../components/lifecycle/panels/BidPanel';
import { ApprovalPanel } from '../components/lifecycle/panels/ApprovalPanel';
import { PlanBuildPanel } from '../components/lifecycle/panels/PlanBuildPanel';
import { ReadinessPanel } from '../components/lifecycle/panels/ReadinessPanel';
import { DeliverPanel } from '../components/lifecycle/panels/DeliverPanel';
import { ScoreChip } from '../components/common/ScoreChip';
import { RecordStatusPill, TrackPill } from '../components/common/StatusPills';
import { usePlatform } from '../contexts/PlatformContext';
import { STAGE_META, trackLabel } from '../utils/stages';
import { entityName } from '../data/entities';
import type { StageId } from '../types/domain';
export function LifecycleWorkspace() {
  const {
    id = ''
  } = useParams();
  const {
    applicationById
  } = usePlatform();
  const record = applicationById(id);
  const [selected, setSelected] = React.useState<StageId | null>(null);
  if (!record) {
    return <div className="p-6" data-id="element-1418" data-mp="1jmkuef88e4cy">
        <EmptyState title="Record not found" description="This lifecycle reference does not exist in the current environment." action={<Link to="/lifecycle" className="inline-flex items-center rounded-sm bg-uae-green px-3 py-2 text-[13px] font-medium text-white" data-id="element-1420" data-mp="1soasu50j63k8">
              Back to portfolio board
            </Link>} data-id="element-1419" data-mp="1jnw5l040aee3" />
      </div>;
  }
  const stage = selected ?? record.stage;
  return <div data-id="element-1421" data-mp="2z3lbnpfb0csa">
      <PageHeader eyebrow={`${trackLabel(record.track)} · ${record.ref} · ${entityName(record.entityId)}`} title={record.eventName} description={`${STAGE_META[stage].label} — ${STAGE_META[stage].blurb}`} actions={<div className="flex flex-wrap items-center gap-2" data-id="element-1423" data-mp="25r529i9yw9xd">
            <RecordStatusPill status={record.status} data-id="element-1424" data-mp="25pk8vvze2x13" />
            <TrackPill track={record.track} data-id="element-1425" data-mp="2ap6kk0zkk57s" />
            <ScoreChip score={record.aiScore} showBand data-id="element-1426" data-mp="2ao3ufva2m6sx" />
            <Link to={`/applications/${record.id}`} className="inline-flex items-center rounded-sm border border-border bg-card px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted" data-id="element-1427" data-mp="1e1ickbt81t1y">
              <FileText className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-1428" data-mp="1mz2fdp6qfxeo" />
              Application record
            </Link>
          </div>} data-id="element-1422" data-mp="2z67fbdz2q858" />

      <div className="space-y-4 p-6" data-id="element-1429" data-mp="ubgggu8g9gy5">
        <StageStepper track={record.track} currentStage={record.stage} selectedStage={stage} daysInStage={record.daysInStage} onSelect={next => setSelected(next)} data-id="element-1430" data-mp="uadnmgrkh4fl" />

        {stage === 'assessment' && <AssessmentPanel record={record} data-id="element-1431" data-mp="mtqj2fekh752" />}
        {stage === 'bid' && <BidPanel bid={record.bid} data-id="element-1432" data-mp="htcbszksi183" />}
        {stage === 'approval' && <ApprovalPanel approval={record.approval} data-id="element-1433" data-mp="hrsxvfmlw29h" />}
        {stage === 'plan-build' && <PlanBuildPanel planBuild={record.planBuild} data-id="element-1434" data-mp="dr91wfdrr4ox" />}
        {stage === 'readiness' && <ReadinessPanel readiness={record.readiness} data-id="element-1435" data-mp="8tzcv0uakv1m" />}
        {stage === 'deliver-operate' && <DeliverPanel delivery={record.delivery} data-id="element-1436" data-mp="41xsssqvu011" />}

        <GatePanel record={record} stage={stage} data-id="element-1437" data-mp="404lrcccxdsi" />
      </div>
    </div>;
}